/**
 * AdPresenter implementation backed by the #ad-overlay DOM. It is a placeholder
 * "ad" (no real network): a countdown that becomes skippable after the
 * configured number of seconds and auto-completes at the configured length.
 * Both values come from config via the ad service, so retuning ad length /
 * skip timing is a single config edit. Dormant in v1 (FEATURES.ads off).
 */
import type { AdPresenter } from '../services/ads/adService';
import { el, hide, show } from './dom';

export class AdOverlayPresenter implements AdPresenter {
  private readonly overlay = el('ad-overlay');
  private readonly timer = el('ad-timer');
  private readonly skipBtn = el<HTMLButtonElement>('ad-skip-btn');

  present(opts: { lengthSec: number; skipAfterSec: number }): Promise<'completed' | 'skipped'> {
    return new Promise((resolve) => {
      const tick = 0.1;
      let elapsed = 0;
      show(this.overlay);
      this.skipBtn.disabled = true;

      const render = (): void => {
        this.timer.textContent = String(Math.max(0, Math.ceil(opts.lengthSec - elapsed)));
        if (elapsed >= opts.skipAfterSec) {
          this.skipBtn.disabled = false;
          this.skipBtn.textContent = 'Skip';
        } else {
          this.skipBtn.textContent = `Skip in ${Math.ceil(opts.skipAfterSec - elapsed)}`;
        }
      };

      const finish = (result: 'completed' | 'skipped'): void => {
        clearInterval(interval);
        this.skipBtn.removeEventListener('click', onSkip);
        hide(this.overlay);
        resolve(result);
      };

      const onSkip = (): void => {
        if (!this.skipBtn.disabled) finish('skipped');
      };

      render();
      this.skipBtn.addEventListener('click', onSkip);
      const interval = setInterval(() => {
        elapsed += tick;
        render();
        if (elapsed >= opts.lengthSec) finish('completed');
      }, tick * 1000);
    });
  }
}
